import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import * as userSlice from '../../data/user.slice';
import { useAppSelector } from '../../hooks/useAppSelector';
import DialogTransition from './DialogTransition';

const PREFIX = 'ESADialog';

const classes = {
  appBar: `${PREFIX}-appBar`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
  loadingWrapper: `${PREFIX}-loadingWrapper`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    position: 'relative',
  },

  [`& .${classes.title}`]: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },

  [`& .${classes.content}`]: {
    border: 'none',
    width: '100%',
    height: '100%',
  },
  [`& .${classes.loadingWrapper}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

interface ESADialogProps {
  open: boolean;
  close: (version: number | null) => void;
}

export default function ESADialog({ open, close }: ESADialogProps) {
  const { t } = useTranslation('common');
  const externalSupportAgreementInfo = useAppSelector(
    userSlice.selectExternalSupportAgreementInfo,
  );

  const [html, setHtml] = useState<string>();
  const [externalSupportAgreementVersion, setExternalSupportAgreementVersion] =
    React.useState<number | null>(null);
  const [isRead, setIsRead] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  React.useEffect(() => {
    if (externalSupportAgreementInfo) {
      setExternalSupportAgreementVersion(
        externalSupportAgreementInfo?.supportAgreementID,
      );
      fetch(
        externalSupportAgreementInfo?.supportAgreementFileNameWithPath,
      ).then((resp: any) =>
        resp.text().then((html: string) => {
          setHtml(html);
          setIsLoading(false);
        }),
      );
    }
  }, [externalSupportAgreementInfo]);

  const handleAccept = () => {
    close(externalSupportAgreementVersion);
  };

  const handleScroll = (evt: any) => {
    const elt = evt.target;
    const scrollPos = elt.offsetHeight + elt.scrollTop;
    if (scrollPos >= elt.scrollHeight - 50) {
      setIsRead(true);
    }
  };

  return (
    <StyledDialog
      open={open}
      TransitionComponent={DialogTransition}
      scroll="paper"
      maxWidth="md">
      <DialogTitle>{t('esa.title')}</DialogTitle>
      <DialogContent onScroll={handleScroll}>
        {isLoading ? (
          <div className={classes.loadingWrapper}>
            <CircularProgress />
          </div>
        ) : (
          <div
            className={classes.content}
            dangerouslySetInnerHTML={{
              __html: html || '',
            }}></div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          color="primary"
          disabled={!isRead}
          onClick={handleAccept}>
          {t('esa.accept')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
